import { useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { adminOrderApi, type ProductAttrValue, type ProductDetail, type StoreProduct } from '@/api/admin-order'

export interface PromotionBundlePickedItem {
  product_id: number
  sku_unique: string
  product_name_snapshot: string
  sku_name_snapshot: string
  product_image_snapshot: string
  unit_price_snapshot: number
  quantity: number
  line_original_price: number
  sort: number
}

interface PromotionBundleProductPickerDialogProps {
  open: boolean
  selectedCount: number
  onOpenChange: (open: boolean) => void
  onSelect: (item: PromotionBundlePickedItem) => void
}

function money(value: number | undefined) {
  return `¥${(value ?? 0).toFixed(2)}`
}

export function PromotionBundleProductPickerDialog({
  open,
  selectedCount,
  onOpenChange,
  onSelect,
}: PromotionBundleProductPickerDialogProps) {
  const [keyword, setKeyword] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedProduct, setSelectedProduct] = useState<StoreProduct | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['promotion-bundles', 'product-picker', searchTerm],
    queryFn: () => adminOrderApi.listProducts(searchTerm),
    enabled: open && searchTerm.length > 0,
  })

  const { data: productDetail, isLoading: isDetailLoading } = useQuery({
    queryKey: ['promotion-bundles', 'product-picker', 'detail', selectedProduct?.id],
    queryFn: () => adminOrderApi.getProductDetail(selectedProduct!.id),
    enabled: open && selectedProduct !== null,
  })

  const handleSearch = useCallback(() => {
    setSearchTerm(keyword.trim())
    setSelectedProduct(null)
  }, [keyword])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSearch()
    }
  }, [handleSearch])

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) {
      setSelectedProduct(null)
    }
  }, [onOpenChange])

  const handleSelectSku = useCallback((detail: ProductDetail, sku: ProductAttrValue) => {
    onSelect({
      product_id: detail.id,
      sku_unique: sku.unique,
      product_name_snapshot: detail.store_name,
      sku_name_snapshot: sku.suk || '默认规格',
      product_image_snapshot: sku.image || detail.image,
      unit_price_snapshot: sku.price,
      quantity: 1,
      line_original_price: sku.price,
      sort: selectedCount + 1,
    })
    handleOpenChange(false)
  }, [handleOpenChange, onSelect, selectedCount])

  const products = data?.list ?? []
  const skus = productDetail?.attr_value ?? []

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[86vh] max-w-[calc(100vw-2rem)] overflow-hidden p-0 lg:max-w-4xl">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>新增商品</DialogTitle>
        </DialogHeader>
        <div className="grid max-h-[calc(86vh-73px)] min-h-[520px] grid-cols-1 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_320px] lg:overflow-hidden">
          <div className="flex min-w-0 flex-col">
            <div className="flex items-center gap-2 border-b p-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="搜索 CRMEB 商品名称"
                  className="pl-9"
                />
              </div>
              <Button type="button" onClick={handleSearch} disabled={keyword.trim().length === 0}>
                搜索
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              <Table className="min-w-[560px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">图片</TableHead>
                    <TableHead>商品名称</TableHead>
                    <TableHead className="w-24 text-right">价格</TableHead>
                    <TableHead className="w-20 text-right">库存</TableHead>
                    <TableHead className="w-20 text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!searchTerm && (
                    <TableRow>
                      <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                        输入关键词后按 Enter 搜索商品
                      </TableCell>
                    </TableRow>
                  )}
                  {searchTerm && isLoading && (
                    <TableRow>
                      <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                        商品加载中...
                      </TableCell>
                    </TableRow>
                  )}
                  {searchTerm && !isLoading && products.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                        未找到匹配商品
                      </TableCell>
                    </TableRow>
                  )}
                  {products.map((product) => (
                    <TableRow
                      key={product.id}
                      className={selectedProduct?.id === product.id ? 'bg-muted/60' : undefined}
                    >
                      <TableCell>
                        <img src={product.image} alt={product.store_name} className="h-10 w-10 rounded object-cover" />
                      </TableCell>
                      <TableCell>
                        <p className="line-clamp-2 font-medium">{product.store_name}</p>
                      </TableCell>
                      <TableCell className="text-right text-red-600">{money(product.price)}</TableCell>
                      <TableCell className="text-right">{product.stock}</TableCell>
                      <TableCell className="text-right">
                        <Button type="button" size="sm" variant="outline" onClick={() => setSelectedProduct(product)}>
                          规格
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <div className="flex min-w-0 flex-col border-t bg-muted/20 lg:border-l lg:border-t-0">
            <div className="border-b p-4">
              <p className="text-sm font-medium">SKU 规格</p>
              <p className="mt-1 text-xs text-muted-foreground">选择一个规格加入组合</p>
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-4">
              {!selectedProduct && (
                <div className="flex h-full items-center justify-center rounded-lg border border-dashed bg-background p-6 text-center text-sm text-muted-foreground">
                  先从左侧选择商品规格
                </div>
              )}
              {selectedProduct && (
                <div className="space-y-4">
                  <div className="flex gap-3 rounded-lg border bg-background p-3">
                    <img src={selectedProduct.image} alt={selectedProduct.store_name} className="h-14 w-14 rounded object-cover" />
                    <div className="min-w-0">
                      <p className="line-clamp-2 text-sm font-medium">{selectedProduct.store_name}</p>
                      <p className="mt-1 text-sm font-semibold text-red-600">{money(selectedProduct.price)}</p>
                    </div>
                  </div>
                  <Separator />
                  {isDetailLoading && (
                    <div className="py-8 text-center text-sm text-muted-foreground">规格加载中...</div>
                  )}
                  {!isDetailLoading && skus.length === 0 && (
                    <div className="rounded-lg border border-dashed bg-background py-8 text-center text-sm text-muted-foreground">
                      该商品暂无可选 SKU
                    </div>
                  )}
                  {!isDetailLoading && skus.length > 0 && (
                    <div className="space-y-2">
                      {skus.map((sku) => (
                        <button
                          type="button"
                          key={sku.unique}
                          className="flex w-full items-center justify-between gap-3 rounded-lg border bg-background p-3 text-left transition-colors hover:bg-accent"
                          onClick={() => productDetail && handleSelectSku(productDetail, sku)}
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            {sku.image ? (
                              <img src={sku.image} alt={sku.suk || '默认规格'} className="h-9 w-9 rounded object-cover" />
                            ) : (
                              <div className="flex h-9 w-9 items-center justify-center rounded bg-muted text-muted-foreground">
                                <Check className="h-4 w-4" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium">{sku.suk || '默认规格'}</p>
                              <p className="text-xs text-muted-foreground">库存 {sku.stock}</p>
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="text-sm font-semibold text-red-600">{money(sku.price)}</p>
                            <Plus className="ml-auto mt-1 h-4 w-4 text-muted-foreground" />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
